/**
 * Lightweight coordination for entity_* IndexedDB activity.
 * Prevents Phase-2 deferred reads from racing multi-store migration/hydrate writes.
 *
 * Note: withDeadline on readers only rejects the Promise — it cannot abort Dexie/IDB.
 * Tracking writers lets boot delay Phase-2 instead of mass soft-timeouts.
 */

/** @type {{ writersActive: number, readersWaiting: number }} */
export const entityStoreActivityState = {
  writersActive: 0,
  readersWaiting: 0
};

/** @type {Set<() => void>} */
const writerIdleWaiters = new Set();

function notifyWriterIdle() {
  if (entityStoreActivityState.writersActive > 0) return;
  for (const resolve of writerIdleWaiters) {
    try {
      resolve();
    } catch {
      /* ignore */
    }
  }
  writerIdleWaiters.clear();
}

/**
 * @param {string} name
 * @param {{ storeCount?: number, fields?: string[] }} [detail]
 */
export function beginEntityStoreWrite(name, detail = {}) {
  entityStoreActivityState.writersActive += 1;
  return {
    name: String(name || 'entity_write'),
    storeCount: Number(detail.storeCount) || 0,
    fields: Array.isArray(detail.fields) ? detail.fields : undefined,
    startedAt: typeof performance !== 'undefined' ? performance.now() : Date.now()
  };
}

/**
 * @param {{ name: string, storeCount: number, startedAt: number }} token
 */
export function endEntityStoreWrite(token) {
  void token;
  entityStoreActivityState.writersActive = Math.max(
    0,
    entityStoreActivityState.writersActive - 1
  );
  notifyWriterIdle();
}

/**
 * Run an entity-store write under the activity lock.
 * @template T
 * @param {string} name
 * @param {{ storeCount?: number, fields?: string[] }} detail
 * @param {() => Promise<T>} fn
 * @returns {Promise<T>}
 */
export async function runEntityStoreWrite(name, detail, fn) {
  const token = beginEntityStoreWrite(name, detail);
  try {
    return await fn();
  } finally {
    endEntityStoreWrite(token);
  }
}

/**
 * Resolve when no entity writers are active, or after maxWaitMs.
 * @param {{ maxWaitMs?: number, label?: string }} [options]
 */
export async function waitForEntityStoreWritersIdle(options = {}) {
  const maxWaitMs = Number.isFinite(options.maxWaitMs) ? options.maxWaitMs : 18000;
  const label = options.label || 'wait_writers';

  if (entityStoreActivityState.writersActive <= 0) {
    return { waited: false, timedOut: false, writersActive: 0, waitedMs: 0 };
  }

  entityStoreActivityState.readersWaiting += 1;
  const startedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();

  try {
    await new Promise((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        writerIdleWaiters.delete(onIdle);
        clearTimeout(timer);
        resolve();
      };
      const onIdle = () => finish();
      writerIdleWaiters.add(onIdle);

      const timer = setTimeout(() => {
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            `[entityStoreActivity] ${label}: writers still active after ${maxWaitMs}ms — continuing`,
            { writersActive: entityStoreActivityState.writersActive }
          );
        }
        finish();
      }, Math.max(0, maxWaitMs));

      if (entityStoreActivityState.writersActive <= 0) finish();
    });
  } finally {
    entityStoreActivityState.readersWaiting = Math.max(
      0,
      entityStoreActivityState.readersWaiting - 1
    );
  }

  const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
  const waitedMs = Math.round(now - startedAt);
  const timedOut = entityStoreActivityState.writersActive > 0;
  return {
    waited: true,
    timedOut,
    writersActive: entityStoreActivityState.writersActive,
    waitedMs
  };
}

/** Test / diagnostics helper — reset counters between tests. */
export function resetEntityStoreActivityStateForTests() {
  entityStoreActivityState.writersActive = 0;
  entityStoreActivityState.readersWaiting = 0;
  writerIdleWaiters.clear();
}
